import DateFnsUtils from '@date-io/date-fns';
import { Button, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { trackPromise } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import axios from '../../../axios/AxiosInterceptors';
import { onError, onSuccess } from '../../../store/actions/popupActions';
import useStyles from '../../../Styles';
import { hasPermissionForAccount } from '../../../Utility/PermissionsUtil';

const MonthlyCorrection = (props) => {

    const classes = useStyles(props);
    const params = useParams();
    const dispatch = useDispatch();
    const roles = useSelector(state => state.auth.roles);
    const [meterAccounts, setMeterAccounts] = useState([{ id: 0, meterName: '' }]);
    const [startDate, setStartDate] = useState(moment().subtract(1, 'months'));
    const [monthlyCorrections, setMonthlyCorrections] = useState([]);
    const [editableDays, setEditableDays] = useState([]);


    useEffect(() => {
        retrieveMeterAccounts();
        retrieveEditableDays(moment(startDate).format("YYYY"), moment(startDate).format("MMMM").toUpperCase());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params])

    const retrieveMeterAccounts = async () => {
        await trackPromise(axios.get("/powermeter/v1/account/" + params.accountId + "/meter?pageNumber=0&pageSize=100").then(response => {
            if (response && response.data && response.data.results) {
                meterAccounts.push(...response.data.results);
                setMeterAccounts([...meterAccounts]);
            }
        })).catch(error => {
            dispatch(onError(error.response));
        })
    }

    const retrieveEditableDays = async (year, month) => {
        if (hasPermissionForAccount(roles, Number(params.accountId), "ROLE_submit_monthly_correction")) {
            await trackPromise(axios.get("/pjm/powermeter/monthlycorrection/window?year=" + year + "&month=" + month).then(response => {
                setEditableDays(response.data);
            })).catch(error => {
                setEditableDays([]);
                dispatch(onError(error.response));
            })
        } else {
            setEditableDays([]);
        }
    }

    const retrieveMonthlyTotals = async () => {
        await trackPromise(axios.get("/powermeter/v1/account/" + params.accountId + "/monthlyCorrection/year/" + moment(startDate).format("YYYY") + "/month/" + moment(startDate).format("MMMM").toUpperCase()).then(response => {
            setMonthlyCorrections([...response.data]);
        }).catch(error => {
            dispatch(onError(error.response));
        }));
    }

    const handleCorrectionChange = (e, id) => {
        for (let monthlyCorrection of monthlyCorrections) {
            if (monthlyCorrection.id === id) {
                monthlyCorrection.correctionTotal = parseFloat(e.value).toFixed(3);
                monthlyCorrection.revisedTotal = parseFloat(Number(monthlyCorrection.originalTotal) + Number(e.value)).toFixed(3);
            }
        }
        setMonthlyCorrections([...monthlyCorrections]);
    }

    const handleRevisedTotalChange = (e, id) => {
        for (let monthlyCorrection of monthlyCorrections) {
            if (monthlyCorrection.id === id) {
                monthlyCorrection.revisedTotal = parseFloat(e.value).toFixed(3);
                monthlyCorrection.correctionTotal = parseFloat(Number(e.value) - Number(monthlyCorrection.originalTotal)).toFixed(3);
            }
        }
        setMonthlyCorrections([...monthlyCorrections]);
    }

    const handleMonthYearChange = (e) => {
        setStartDate(e);
        retrieveEditableDays(moment(e).format("YYYY"), moment(e).format("MMMM").toUpperCase())
    }

    const saveMonthlyCorrection = async () => {
        await trackPromise(axios.post("/powermeter/v1/account/" + params.accountId + "/monthlyCorrection/year/" + moment(startDate).format("YYYY") + "/month/" + moment(startDate).format("MMMM").toUpperCase(), JSON.stringify(monthlyCorrections)).then(response => {
            setMonthlyCorrections([...response.data]);
            dispatch(onSuccess("Monthly Corrections updated"));
        }).catch(error => {
            dispatch(onError(error.response));
        }));
    }

    const isDisabled = () => {
        return !(editableDays.find(r => r === moment().format("yyyy-MM-DD"))) && hasPermissionForAccount(roles, Number(params.accountId), "ROLE_submit_meter_values");
    }

    return (
        <main className={classes.layout}>
            <Typography variant="h4" align='center'>Monthly Correction</Typography>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={3} />
                <Grid item xs={6} sm={3}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker variant="inline" disableToolbar onChange={(e) => handleMonthYearChange(e)} value={startDate} autoOk={true} margin="normal" views={['year', 'month']} format="MM/yyyy" />
                    </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={6} sm={1} />
                <Grid item xs={6} sm={3}>
                    <Button onClick={retrieveMonthlyTotals}>Retrieve</Button>
                </Grid>
            </Grid>
            <TableContainer style={{ marginBottom: 20 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>PJM Meter Account ID</TableCell>
                            <TableCell>Meter Name</TableCell>
                            <TableCell>Month</TableCell>
                            <TableCell>Year</TableCell>
                            <TableCell>Original Value (MWh)</TableCell>
                            <TableCell>Correction (MWh)</TableCell>
                            <TableCell>Revised Total (MWh)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {monthlyCorrections.map(r => {
                            return (
                                <TableRow key={r.id}>
                                    <TableCell>{r.pjmMeterAccountId}</TableCell>
                                    <TableCell>{r.meterAccountName}</TableCell>
                                    <TableCell>{r.month}</TableCell>
                                    <TableCell>{r.year}</TableCell>
                                    <TableCell><NumberFormat value={r.originalTotal} customInput={TextField} thousandSeparator={true} decimalScale={3} type="text" disabled={true} fixedDecimalScale /></TableCell>
                                    <TableCell><NumberFormat value={r.correctionTotal} customInput={TextField} thousandSeparator={true} decimalScale={3} type="text" onValueChange={(e) => handleCorrectionChange(e, r.id)} disabled={isDisabled()} fixedDecimalScale /></TableCell>
                                    <TableCell><NumberFormat value={r.revisedTotal} customInput={TextField} thousandSeparator={true} decimalScale={3} type="text" onValueChange={(e) => handleRevisedTotalChange(e, r.id)} disabled={isDisabled()} fixedDecimalScale /></TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Grid container spacing={2}>
                <Grid item xs={4} />
                <Grid item xs={4} align='center'>
                    <Button onClick={saveMonthlyCorrection}>Submit</Button>
                </Grid>
                <Grid item xs={4} />
            </Grid>
        </main>
    );
}

export default MonthlyCorrection;